import { Component } from '@theme/component';

class MerseyProductVideos extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.bindVideos();
  }

  updatedCallback() {
    super.updatedCallback();
    this.bindVideos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  cleanup() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.cancelMovement();
    clearTimeout(this.rotationTimer);
    for (const video of this.videos ?? []) video.pause();
  }

  bindVideos() {
    this.cleanup();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.videos = [...this.querySelectorAll('video')];
    this.rail = this.querySelector('.ma-product-videos__rail');
    this.toggle = this.querySelector('[data-video-toggle]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.ratios = new Map();
    this.blocked = new Set();
    this.pending = new Set();
    this.audioVideo = null;
    this.paused = this.motion.matches;
    this.rotationStopped = false;
    this.toggle.hidden = false;
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('focusin', event => {
      if (!event.target.closest('[data-video-toggle]')) this.stopRotation();
    }, { signal });
    this.rail.addEventListener('pointerdown', this.stopRotation, { signal });
    this.rail.addEventListener('wheel', this.stopRotation, { passive: true, signal });
    this.motion.addEventListener('change', this.onMotion, { signal });
    document.addEventListener('play', this.onPlay, { capture: true, signal });
    document.addEventListener('visibilitychange', this.onVisibility, { signal });
    window.addEventListener('pagehide', this.onPageHide, { signal });
    window.addEventListener('pageshow', this.onVisibility, { signal });
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        this.ratios.set(entry.target, entry.intersectionRatio);
        if (entry.intersectionRatio < 0.5 && this.audioVideo === entry.target) {
          this.audioVideo.muted = true;
          this.audioVideo = null;
        }
      }
      this.syncPlayback();
    }, { threshold: [0, 0.5] });
    for (const video of this.videos) {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.controls = false;
      video.parentElement.querySelector('[data-video-play]').hidden = false;
      video.addEventListener('volumechange', () => {
        if (!video.muted) {
          this.audioVideo = video;
          this.stopRotation();
        } else if (this.audioVideo === video) {
          this.audioVideo = null;
        }
        this.syncPlayback();
      }, { signal });
      this.observer.observe(video);
    }
    this.updateControls();
  }

  stopRotation = () => {
    this.rotationStopped = true;
    clearTimeout(this.rotationTimer);
    this.cancelMovement();
    this.updateControls();
  };

  onMotion = () => {
    this.paused = this.motion.matches;
    this.rotationStopped = this.motion.matches;
    this.silence();
    this.syncPlayback();
  };

  silence() {
    this.audioVideo = null;
    for (const video of this.videos) video.muted = true;
  }

  onPageHide = () => {
    this.silence();
    this.cancelMovement();
    clearTimeout(this.rotationTimer);
    for (const video of this.videos) video.pause();
  };

  onVisibility = () => {
    if (document.hidden) {
      this.silence();
      this.cancelMovement();
    }
    this.syncPlayback();
  };

  onPlay = event => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    if (!this.contains(event.target)) {
      this.paused = true;
      this.silence();
      this.syncPlayback();
    }
  };

  shouldPlay(video) {
    return !document.hidden && this.isConnected && (this.ratios.get(video) ?? 0) >= 0.5 &&
      (this.audioVideo ? this.audioVideo === video : !this.paused);
  }

  play(video) {
    if (!video.paused || this.pending.has(video) || this.blocked.has(video)) return;
    const signal = this.controller.signal;
    this.pending.add(video);
    video.play().then(() => {
      if (signal.aborted) return;
      if (!this.shouldPlay(video)) video.pause();
    }).catch(error => {
      if (signal.aborted || error.name === 'AbortError') return;
      this.blocked.add(video);
      if (this.audioVideo === video) this.silence();
      this.updateControls();
    }).finally(() => {
      if (signal.aborted) return;
      this.pending.delete(video);
      // A quick pause/resume can finish while the previous play request is settling.
      if (this.shouldPlay(video) && video.paused && !this.blocked.has(video)) this.play(video);
    });
  }

  syncPlayback() {
    for (const video of this.videos) {
      if (this.shouldPlay(video)) this.play(video);
      else video.pause();
    }
    this.updateControls();
    this.scheduleRotation();
  }

  updateControls() {
    if (!this.toggle) return;
    const stopped = this.paused || this.rotationStopped || !!this.audioVideo;
    this.toggle.setAttribute('aria-label', stopped ? this.toggle.dataset.resume : this.toggle.dataset.pause);
    this.toggle.querySelector('[data-video-pause-icon]').hidden = stopped;
    this.toggle.querySelector('[data-video-resume-icon]').hidden = !stopped;
    for (const video of this.videos) {
      const button = video.parentElement.querySelector('[data-video-play]');
      const audible = video === this.audioVideo;
      const needsPlay = this.paused || this.blocked.has(video);
      button.dataset.audible = String(audible);
      button.setAttribute('aria-label', audible ? button.dataset.mute : needsPlay ? button.dataset.play : button.dataset.listen);
      button.querySelector('[data-video-label]').textContent = audible ? button.dataset.muteShort : needsPlay ? button.dataset.playShort : button.dataset.listenShort;
      video.controls = audible;
    }
  }

  scheduleRotation() {
    clearTimeout(this.rotationTimer);
    if (this.moving || this.paused || this.rotationStopped || this.audioVideo || this.motion.matches || document.hidden ||
      !this.videos.some(video => this.shouldPlay(video) && !this.blocked.has(video)) ||
      this.rail.scrollWidth <= this.rail.clientWidth + 2) return;
    this.rotationTimer = setTimeout(() => this.moveCarousel(1), 4000);
  }

  cancelMovement() {
    cancelAnimationFrame(this.animationFrame);
    this.moving = false;
    this.rail?.removeAttribute('data-rotating');
  }

  moveCarousel(direction) {
    this.cancelMovement();
    const cards = [...this.rail.children];
    if (cards.length < 2) return;
    const first = cards[0].getBoundingClientRect();
    const second = cards[1].getBoundingClientRect();
    const step = Math.abs(second.left - first.left);
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    if (!step || max <= 2) return;
    const sign = getComputedStyle(this.rail).direction === 'rtl' ? -1 : 1;
    this.moving = true;
    this.rail.setAttribute('data-rotating', '');
    let offset = Math.abs(this.rail.scrollLeft);
    const looping = max >= step;
    if (looping) {
      // Recycle original cards outside the viewport; do not clone or reload videos.
      while (offset >= step - 1) {
        this.rail.append(this.rail.firstElementChild);
        offset = Math.max(0, offset - step);
        this.rail.scrollLeft = sign * offset;
      }
      if (direction < 0 && offset < 1) {
        this.rail.prepend(this.rail.lastElementChild);
        offset += step;
        this.rail.scrollLeft = sign * offset;
      }
    }
    const start = offset;
    const end = looping ? (direction > 0 ? step : 0) : (offset >= max - 1 ? 0 : max);
    const finish = () => {
      if (looping && direction > 0) {
        this.rail.append(this.rail.firstElementChild);
        this.rail.scrollLeft = 0;
      }
      this.moving = false;
      this.rail.removeAttribute('data-rotating');
      this.syncPlayback();
    };
    if (this.motion.matches) {
      this.rail.scrollLeft = sign * end;
      finish();
      return;
    }
    let started;
    const animate = now => {
      started ??= now;
      const progress = Math.min((now - started) / 700, 1);
      const eased = (1 - Math.cos(Math.PI * progress)) / 2;
      this.rail.scrollLeft = sign * (start + (end - start) * eased);
      if (progress < 1) this.animationFrame = requestAnimationFrame(animate);
      else finish();
    };
    this.animationFrame = requestAnimationFrame(animate);
  }

  onClick = event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-video-toggle]')) {
      const resume = this.paused || this.rotationStopped || !!this.audioVideo;
      this.silence();
      this.paused = !resume;
      if (!resume) this.cancelMovement();
      this.rotationStopped = !resume;
      this.blocked.clear();
      this.syncPlayback();
      return;
    }
    const direction = event.target.closest('[data-video-direction]');
    if (direction) {
      this.stopRotation();
      this.moveCarousel(Number(direction.dataset.videoDirection));
      return;
    }
    const button = event.target.closest('[data-video-play]');
    if (!button) return;
    const video = button.parentElement.querySelector('video');
    if (this.audioVideo === video) {
      this.silence();
      this.paused = this.motion.matches;
    } else {
      this.silence();
      this.audioVideo = video;
      this.stopRotation();
      this.blocked.delete(video);
      video.muted = false;
      video.controls = true;
      // Call play synchronously in the tap handler for Safari's user-gesture policy.
      this.play(video);
    }
    this.syncPlayback();
  };
}

if (!customElements.get('ma-product-videos')) {
  customElements.define('ma-product-videos', MerseyProductVideos);
}
